import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import {
    ArrowLeft,
    BookOpen,
    Clock,
    Briefcase,
    ExternalLink,
    Sparkles,
    Lightbulb,
    Code2,
    Terminal,
    Loader,
    ChevronDown,
    Layers,
    Zap,
    Hash,
    GripVertical,
    PanelRightOpen,
    PanelRightClose
} from 'lucide-react';
import { careerAPI } from '../utils/api';
import SEO from '../components/SEO';

// ─── Table of Contents Extractor ─────────────────────────────────────────────
const extractHeadings = (markdown) => {
    if (!markdown) return [];
    const lines = markdown.split('\n');
    const headings = [];
    lines.forEach((line, i) => {
        const match = line.match(/^(#{1,3})\s+(.+)/);
        if (match) {
            headings.push({
                level: match[1].length,
                text: match[2].replace(/[*_`]/g, ''),
                id: match[2].replace(/[^a-zA-Z0-9\s]/g, '').trim().toLowerCase().replace(/\s+/g, '-'),
                line: i
            });
        }
    });
    return headings;
};

// ─── Reading Progress Bar ────────────────────────────────────────────────────
const ReadingProgress = () => {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            setProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    return (
        <div className="fixed top-0 left-0 right-0 h-[3px] z-[100] bg-transparent">
            <motion.div
                className="h-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 shadow-[0_0_15px_rgba(16,185,129,0.5)]"
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.1 }}
            />
        </div>
    );
};

// ─── Custom Heading Renderer for Anchor IDs ──────────────────────────────────
const HeadingRenderer = ({ level, children }) => {
    const text = typeof children === 'string' ? children :
        Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : c?.props?.children || '').join('') : '';
    const id = text.replace(/[^a-zA-Z0-9\s]/g, '').trim().toLowerCase().replace(/\s+/g, '-');
    const Tag = `h${level}`;
    return <Tag id={id}>{children}</Tag>;
};

const TaskDeepDivePage = () => {
    const { roadmapId, weekIndex, taskIndex } = useParams();
    const navigate = useNavigate();
    const contentRef = useRef(null);

    const [taskData, setTaskData] = useState(null);
    const [roadmapContext, setRoadmapContext] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showPanel, setShowPanel] = useState(true);
    const [activeHeading, setActiveHeading] = useState('');

    // ─── Load context from localStorage ──────────────────────────────────────
    useEffect(() => {
        try {
            const activeRoadmapStr = localStorage.getItem('roadmapData');
            let contextRoadmap = null;

            if (activeRoadmapStr) {
                const active = JSON.parse(activeRoadmapStr);
                if (
                    roadmapId === 'current' ||
                    !roadmapId ||
                    active.id === roadmapId ||
                    String(active.roadmap_id) === String(roadmapId)
                ) {
                    contextRoadmap = active;
                }
            }

            if (!contextRoadmap) {
                toast.error("Roadmap context not found");
                navigate(`/roadmap/${roadmapId || ''}`);
                return;
            }

            setRoadmapContext(contextRoadmap);
            const pWeekId = parseInt(weekIndex, 10);
            const pTaskId = parseInt(taskIndex, 10);

            if (contextRoadmap.weeks && contextRoadmap.weeks[pWeekId] && contextRoadmap.weeks[pWeekId].tasks[pTaskId]) {
                setTaskData(contextRoadmap.weeks[pWeekId].tasks[pTaskId]);
            } else {
                toast.error("Task not found");
                navigate(`/roadmap/${roadmapId || ''}`);
            }
        } catch (e) {
            console.error("Error loading task context", e);
            toast.error("Failed to load task");
            navigate('/dashboard');
        }
    }, [roadmapId, weekIndex, taskIndex, navigate]);

    // ─── Generate deep dive if missing ───────────────────────────────────────
    useEffect(() => {
        const fetchDeepDive = async () => {
            if (!taskData || !roadmapContext) return;
            if (taskData.detailed_explanation && taskData.detailed_explanation.length > 300) return;

            setIsGenerating(true);
            try {
                const payload = {
                    task_title: taskData.title,
                    task_description: taskData.description,
                    week_title: roadmapContext.weeks[parseInt(weekIndex, 10)].title,
                    career_path: roadmapContext.career_path || roadmapContext.careerPath,
                    tech_stack: null,
                    roadmap_id: roadmapId,
                    week_index: parseInt(weekIndex, 10),
                    task_index: parseInt(taskIndex, 10)
                };

                const res = await careerAPI.generateTaskDeepDive(payload);
                if (res && res.markdown) {
                    setTaskData(prev => ({ ...prev, detailed_explanation: res.markdown }));
                    try {
                        const activeRoadmapStr = localStorage.getItem('roadmapData');
                        if (activeRoadmapStr) {
                            const active = JSON.parse(activeRoadmapStr);
                            active.weeks[parseInt(weekIndex, 10)].tasks[parseInt(taskIndex, 10)].detailed_explanation = res.markdown;
                            localStorage.setItem('roadmapData', JSON.stringify(active));
                        }
                    } catch (cacheErr) {
                        console.warn("Failed to update local cache", cacheErr);
                    }
                }
            } catch (err) {
                console.error("Deep dive generation failed", err);
                toast.error("Failed to generate tutorial. Please try again.");
            } finally {
                setIsGenerating(false);
            }
        };
        fetchDeepDive();
    }, [taskData?.title, roadmapContext?.career_path]);

    // ─── Track active heading on scroll ──────────────────────────────────────
    useEffect(() => {
        if (!taskData?.detailed_explanation) return;
        const headings = extractHeadings(taskData.detailed_explanation);
        if (headings.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveHeading(entry.target.id);
                    }
                });
            },
            { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
        );

        const timer = setTimeout(() => {
            headings.forEach((h) => {
                const el = document.getElementById(h.id);
                if (el) observer.observe(el);
            });
        }, 500);

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [taskData?.detailed_explanation]);

    const headings = taskData?.detailed_explanation ? extractHeadings(taskData.detailed_explanation) : [];

    // ─── Full Page Loading State ─────────────────────────────────────────────
    if (!taskData) {
        return (
            <div className="min-h-screen bg-[#08090c] flex items-center justify-center">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
                    <Loader className="w-8 h-8 text-emerald-500 animate-spin" />
                    <p className="text-slate-500 font-mono text-sm">Loading task context...</p>
                </motion.div>
            </div>
        );
    }

    const typeColor = taskData.type === 'video' ? 'red' : taskData.type === 'reading' ? 'blue' : taskData.type === 'practice' ? 'emerald' : 'purple';

    return (
        <div className="min-h-screen bg-[#08090c] text-slate-200 font-sans">
            <SEO
                title={`${taskData.title} | Deep Dive`}
                description={`AI-generated tutorial: ${taskData.title}`}
            />
            <ReadingProgress />

            {/* ═══ Fixed Top Toolbar ═══ */}
            <div className="fixed top-0 left-0 right-0 z-50 h-14 bg-[#0c0e12]/90 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="h-full max-w-[2000px] mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
                    {/* Left: Back + breadcrumb */}
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-1.5 text-slate-500 hover:text-white transition-colors flex-shrink-0 group"
                        >
                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                            <span className="text-xs font-medium hidden sm:inline">Back</span>
                        </button>
                        <div className="h-4 w-px bg-white/10 flex-shrink-0" />
                        <span className="text-xs text-slate-600 font-mono truncate hidden md:inline">
                            {roadmapContext?.career_path || 'Roadmap'} / Week {parseInt(weekIndex, 10) + 1}
                        </span>
                        <div className="h-4 w-px bg-white/10 flex-shrink-0 hidden md:block" />
                        <span className="text-sm font-semibold text-white truncate max-w-xs lg:max-w-md">
                            {taskData.title}
                        </span>
                    </div>

                    {/* Right: Meta pills + panel toggle */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase tracking-wider bg-${typeColor}-500/10 text-${typeColor}-400 border border-${typeColor}-500/20 hidden sm:inline-block`}>
                            {taskData.type}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded font-mono text-slate-500 border border-white/5 hidden sm:flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {taskData.duration}
                        </span>
                        <button
                            onClick={() => setShowPanel(!showPanel)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all"
                            title={showPanel ? 'Hide sidebar' : 'Show sidebar'}
                        >
                            {showPanel ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* ═══ Main Layout: Content + Optional Right Panel ═══ */}
            <div className="pt-14 flex w-full min-h-[calc(100vh-56px)]">

                {/* ─── Content Column ─── */}
                <div className={`flex-1 min-w-0 transition-all duration-300 ${showPanel ? 'xl:mr-0' : ''}`}>

                    {/* Hero Section: Full-bleed gradient bar */}
                    <div className="relative overflow-hidden border-b border-white/[0.04]">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/[0.04] via-transparent to-blue-500/[0.04]" />
                        <div className="absolute top-0 left-1/4 w-[500px] h-[300px] bg-emerald-500/[0.06] rounded-full blur-[100px] pointer-events-none" />
                        <div className="absolute bottom-0 right-1/4 w-[400px] h-[250px] bg-blue-500/[0.06] rounded-full blur-[100px] pointer-events-none" />

                        <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                                <div className="flex items-center gap-3 mb-6 flex-wrap">
                                    <span className={`text-[10px] px-2.5 py-1 rounded-md font-mono font-bold uppercase tracking-widest bg-${typeColor}-500/10 text-${typeColor}-400 border border-${typeColor}-500/20`}>
                                        {taskData.type}
                                    </span>
                                    <span className="text-xs font-mono text-slate-500 flex items-center gap-1.5 bg-white/[0.03] border border-white/[0.06] px-2.5 py-1 rounded-md">
                                        <Clock className="w-3 h-3" />
                                        {taskData.duration}
                                    </span>
                                    <span className="text-xs font-mono text-slate-600 flex items-center gap-1.5">
                                        <Layers className="w-3 h-3" />
                                        Week {parseInt(weekIndex, 10) + 1}, Task {parseInt(taskIndex, 10) + 1}
                                    </span>
                                </div>

                                <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-[1.1] tracking-tight mb-6 max-w-4xl">
                                    {taskData.title}
                                </h1>
                                <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-3xl font-light">
                                    {taskData.description}
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Tutorial Content */}
                    <div ref={contentRef} className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
                        {isGenerating ? (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8">
                                {/* Terminal-style generation indicator */}
                                <div className="bg-[#0c0e14] border border-[#1a1e28] rounded-2xl overflow-hidden shadow-2xl">
                                    <div className="bg-[#10131a] border-b border-[#1a1e28] px-5 py-3.5 flex items-center gap-4">
                                        <div className="flex gap-2">
                                            <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                                            <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                                            <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-400/70 bg-emerald-500/[0.08] px-3 py-1 rounded border border-emerald-500/10">
                                            <Loader className="w-3 h-3 animate-spin" />
                                            rastorai generate --deep-dive
                                        </div>
                                    </div>
                                    <div className="p-8 sm:p-12 space-y-6">
                                        <div className="flex items-center gap-4 mb-8">
                                            <div className="w-12 h-12 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20 flex items-center justify-center animate-pulse">
                                                <Sparkles className="w-6 h-6 text-emerald-500" />
                                            </div>
                                            <div>
                                                <p className="text-base font-mono font-semibold text-slate-300">Generating comprehensive tutorial</p>
                                                <p className="text-sm font-mono text-slate-600">~500 words · code examples · best practices</p>
                                            </div>
                                        </div>
                                        {/* Shimmer skeleton */}
                                        <div className="space-y-5 animate-pulse">
                                            <div className="h-7 bg-[#151821] rounded-lg w-2/5" />
                                            <div className="h-4 bg-[#13161e] rounded w-full" />
                                            <div className="h-4 bg-[#13161e] rounded w-11/12" />
                                            <div className="h-4 bg-[#13161e] rounded w-full" />
                                            <div className="h-4 bg-[#13161e] rounded w-3/4 mb-8" />
                                            <div className="h-6 bg-[#151821] rounded-lg w-1/4" />
                                            <div className="h-36 bg-[#0f1218] border border-[#1a1e28] rounded-xl w-full" />
                                            <div className="h-4 bg-[#13161e] rounded w-10/12" />
                                            <div className="h-4 bg-[#13161e] rounded w-full" />
                                            <div className="h-4 bg-[#13161e] rounded w-7/12" />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.article
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                {taskData.detailed_explanation ? (
                                    <div className="
                                        prose prose-invert prose-lg max-w-none
                                        prose-p:text-[#b0b8c9] prose-p:leading-[1.95] prose-p:text-[17px] prose-p:mb-7
                                        prose-headings:text-white prose-headings:tracking-tight prose-headings:font-extrabold
                                        prose-h1:text-4xl prose-h1:mt-14 prose-h1:mb-8 prose-h1:pb-5 prose-h1:border-b prose-h1:border-white/[0.06]
                                        prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-6 prose-h2:pb-4 prose-h2:border-b prose-h2:border-white/[0.06]
                                        prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-5
                                        prose-li:text-[17px] prose-ul:text-[#b0b8c9] prose-li:my-2 prose-li:leading-[1.9]
                                        prose-strong:text-white prose-strong:font-semibold
                                        prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline
                                        prose-code:text-emerald-300 prose-code:bg-emerald-500/[0.08] prose-code:px-2 prose-code:py-0.5 prose-code:rounded-md prose-code:before:content-none prose-code:after:content-none prose-code:font-mono prose-code:text-[15px] prose-code:font-normal
                                        prose-pre:bg-[#0a0c12] prose-pre:border prose-pre:border-white/[0.06] prose-pre:shadow-[inset_0_1px_8px_rgba(0,0,0,0.4)] prose-pre:rounded-xl prose-pre:p-6 prose-pre:my-8 prose-pre:overflow-x-auto
                                        prose-blockquote:border-l-[3px] prose-blockquote:border-emerald-500/60 prose-blockquote:bg-emerald-500/[0.04] prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-8 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-slate-300
                                        prose-hr:border-white/[0.06] prose-hr:my-12
                                        prose-img:rounded-xl prose-img:shadow-xl
                                    ">
                                        <ReactMarkdown
                                            components={{
                                                h1: ({ children }) => <HeadingRenderer level={1}>{children}</HeadingRenderer>,
                                                h2: ({ children }) => <HeadingRenderer level={2}>{children}</HeadingRenderer>,
                                                h3: ({ children }) => <HeadingRenderer level={3}>{children}</HeadingRenderer>,
                                            }}
                                        >
                                            {taskData.detailed_explanation}
                                        </ReactMarkdown>
                                    </div>
                                ) : (
                                    <div className="text-center py-24">
                                        <Terminal className="w-14 h-14 text-slate-700 mx-auto mb-5" />
                                        <h3 className="text-xl font-bold text-slate-500 mb-2">Generation Failed</h3>
                                        <p className="text-slate-600">Could not render tutorial. Please navigate back and try again.</p>
                                    </div>
                                )}

                                {/* Real World Example — inline, not sidebar */}
                                {taskData.real_world_example && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 }}
                                        className="mt-16 p-8 sm:p-10 rounded-2xl bg-gradient-to-br from-indigo-500/[0.06] to-transparent border border-indigo-500/[0.12] relative overflow-hidden"
                                    >
                                        <div className="absolute -top-10 -right-10 w-48 h-48 bg-indigo-500/[0.08] rounded-full blur-[60px] pointer-events-none" />
                                        <div className="flex items-center gap-3 mb-5 relative z-10">
                                            <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                                                <Briefcase className="w-5 h-5 text-indigo-400" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-mono font-bold text-indigo-400/80 uppercase tracking-[0.15em]">Industry Context</p>
                                                <h4 className="text-lg font-bold text-white -mt-0.5">How This Is Used In Production</h4>
                                            </div>
                                        </div>
                                        <p className="text-[#9ba3b4] leading-[1.85] text-base relative z-10">
                                            {taskData.real_world_example}
                                        </p>
                                    </motion.div>
                                )}
                            </motion.article>
                        )}
                    </div>
                </div>

                {/* ─── Right Panel (Collapsible) ─── */}
                <AnimatePresence>
                    {showPanel && (
                        <motion.aside
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 320, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            className="hidden xl:flex flex-col flex-shrink-0 border-l border-white/[0.04] bg-[#0a0c10]/80 backdrop-blur-xl overflow-hidden sticky top-14 h-[calc(100vh-56px)]"
                        >
                            <div className="flex flex-col h-full overflow-y-auto w-[320px] scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent">

                                {/* Table of Contents */}
                                {headings.length > 0 && !isGenerating && (
                                    <div className="p-5 border-b border-white/[0.04]">
                                        <h4 className="text-[10px] font-mono font-bold text-slate-600 uppercase tracking-[0.15em] mb-4 flex items-center gap-2">
                                            <Hash className="w-3 h-3" />
                                            On This Page
                                        </h4>
                                        <nav className="flex flex-col gap-0.5">
                                            {headings.map((h, i) => (
                                                <a
                                                    key={i}
                                                    href={`#${h.id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }}
                                                    className={`text-[13px] py-1.5 rounded transition-all truncate block ${h.level === 1 ? 'pl-0 font-semibold' : h.level === 2 ? 'pl-4' : 'pl-8 text-[12px]'
                                                        } ${activeHeading === h.id
                                                            ? 'text-emerald-400'
                                                            : 'text-slate-500 hover:text-slate-300'
                                                        }`}
                                                >
                                                    {h.text}
                                                </a>
                                            ))}
                                        </nav>
                                    </div>
                                )}

                                {/* Pro Tip */}
                                {taskData.tips && (
                                    <div className="p-5 border-b border-white/[0.04]">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Lightbulb className="w-4 h-4 text-amber-400" />
                                            <h4 className="text-[10px] font-mono font-bold text-amber-400/80 uppercase tracking-[0.15em]">Pro Tip</h4>
                                        </div>
                                        <p className="text-[13px] text-amber-100/60 leading-relaxed">
                                            {taskData.tips}
                                        </p>
                                    </div>
                                )}

                                {/* Resources */}
                                {taskData.resources && taskData.resources.length > 0 && (
                                    <div className="p-5">
                                        <h4 className="text-[10px] font-mono font-bold text-slate-600 uppercase tracking-[0.15em] mb-4 flex items-center gap-2">
                                            <BookOpen className="w-3 h-3" />
                                            Resources
                                        </h4>
                                        <div className="flex flex-col gap-2">
                                            {taskData.resources.map((resource, idx) => {
                                                const isString = typeof resource === 'string';
                                                let url = isString ? (resource.split(' - ')[0] || resource) : resource.url;
                                                let title = isString ? (resource.split(' - ')[1] || 'External Link') : (resource.title || 'Resource');
                                                let domain = 'Website';
                                                try { domain = new URL(url).hostname.replace('www.', ''); } catch (e) { }

                                                return (
                                                    <a
                                                        key={idx}
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="group flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.03] border border-transparent hover:border-white/[0.04] transition-all"
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5 text-slate-600 group-hover:text-emerald-400 mt-0.5 flex-shrink-0 transition-colors" />
                                                        <div className="min-w-0">
                                                            <span className="text-[13px] font-medium text-slate-400 group-hover:text-white transition-colors block truncate">
                                                                {title}
                                                            </span>
                                                            <span className="text-[11px] font-mono text-slate-600 block">
                                                                {domain}
                                                            </span>
                                                        </div>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.aside>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default TaskDeepDivePage;
